import { React, Link, useNavigate, useState, dash, CssBaseline, TextField, Typography, Box, Grid2, rsk_logo } from '../utils/globalImports';
import { CustomButton, Footer, Toaster } from '../utils/globalImports'; // Import Toaster
import useApi from '../hooks/useApi';

const VerifyOtp: React.FC = () => {
    const navigate = useNavigate();
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [openToaster, setOpenToaster] = useState(false); // Toaster visibility state
    const { execute, loading, error } = useApi();
    const [isOtpVerified, setIsOtpVerified] = useState(false); // State to track OTP verification status
    const [resendMessage, setResendMessage] = useState('');
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendTimer, setResendTimer] = useState(30);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    
        if (!validateOtp(otp)) {
        setResendMessage(''); // Clear any previous resend message
          setOtpError('Please enter a valid OTP.');
          return;
        }
        
        setOtpError('');
        setSuccessMessage('');
    
        const email = localStorage.getItem('email');
        if (!email) {
          setOtpError('An error occurred. Please try again.');
          return;
        }
    
        const response = await execute('post', 'Auth/verify-otp', { otp, email });
    
        if (response.data) {
          setSuccessMessage('OTP verified successfully!');
          setIsOtpVerified(true); // Mark OTP as verified
          setOpenToaster(true); // Show toaster when OTP is verified
          localStorage.setItem('resetPasswordFlag', `true`);
          setTimeout(() => {
            setOpenToaster(false); // Hide toaster
            navigate(`/reset-password?email=${encodeURIComponent(email)}`); // Redirect to reset-password page
          }, 3000);
        } else if (response.error) {
          setOtpError(response.error === 'Invalid OTP.' ? 'The OTP is incorrect. Please try again.' : response.error);
        }
      };       

    const validateOtp = (otp: string) => {
        const otpRegex = /^[A-Za-z0-9]{6}$/;
        return otpRegex.test(otp);
    };

    const handleResendOtp = async () => {
        const email = localStorage.getItem('email');
        if (!email) {
          setResendMessage('Unable to resend OTP. Please try again.');
          return;
        }
        setOtpError(''); // Clear any previous OTP error
        const response = await execute('post', 'Auth/forgot-password', { email });
      
        if (response.data) {
          setResendMessage('OTP has been resent to your email.');
          startResendCooldown(); // Start the timer
        } else {
          setResendMessage(response.error || 'Failed to resend OTP.');
        }
      };
      
      const startResendCooldown = () => {
        setResendDisabled(true);
        setResendTimer(30);
        const interval = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              return 30;
            }
            return prev - 1;
          });
        }, 1000);
      };      

    return (
        <>
            <CssBaseline />
            <Grid2 container className="login-container" spacing={2}>
                {/* Left Section */}
                <Grid2 className="left-section">
                    <Box className="header">
                        <Typography component="h1" variant="h5" className="title">
                            Enter OTP
                        </Typography>
                        <img src={dash} alt="Dash" className="dash" />
                    </Box>
                    <Typography className="subheader">
                        Please enter the OTP sent to your email address
                    </Typography>
                    <Box component="form" noValidate onSubmit={handleSubmit}>
                        <Typography className="input-label">OTP</Typography>
                        <TextField
                            margin="normal"
                            fullWidth
                            id="otp"
                            name="otp"
                            placeholder="Enter OTP"
                            autoComplete="off"
                            autoFocus
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            className="text-field"
                        />
                        {successMessage && (
                            <Typography className="success-message" color="primary">
                                {successMessage}
                            </Typography>
                        )}
                        {otpError && (
                            <Typography className="error-message" color="error">
                                {otpError}
                            </Typography>
                        )}
                        {resendMessage && (
                            <Typography
                                className="success-message"
                                color="primary"
                                sx={{ mt: 1 }} // Slight margin top for spacing
                            >
                                {resendMessage}
                            </Typography>
                        )}
                        <CustomButton 
                            capsule 
                            className="login-button" 
                            disabled={loading || isOtpVerified} // Disable button if OTP is verified or loading
                        >
                            {loading ? 'Verifying...' : isOtpVerified ? 'OTP Verified Successfully' : 'Verify OTP'}
                        </CustomButton>
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Typography
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!resendDisabled) handleResendOtp();
                                }}
                                sx={{
                                    cursor: resendDisabled ? 'not-allowed' : 'pointer',
                                    color: resendDisabled ? 'text.disabled' : 'primary.main',
                                    textDecoration: 'underline',
                                    fontSize: '14px',
                                }}
                            >
                                {resendDisabled ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                            </Typography>
                        </Box>
                        <Link to="/login" className="login-link">
                            Return to Login
                        </Link>
                    </Box>
                    
                </Grid2>

                {/* Right Section */}
                <Grid2 className="right-section">
                    <img src={rsk_logo} alt="Logo" className="logo" />
                    <Typography className="title">
                        Start managing your finance faster and better
                    </Typography>
                </Grid2>
            </Grid2>
            <Footer />

            {/* Toaster Notification */}
            <Toaster 
                open={openToaster} 
                message="OTP verified successfully!" 
                type="success" 
                onClose={() => setOpenToaster(false)} 
            />
        </>
    );
};

export default VerifyOtp;